import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useSyncExternalStore, } from 'react';
import { PermissionsContext } from '@shared/hooks';
const DEALER_STORE_KEY = '__DEALER_STORE__';
const STORE_REGISTERED_EVENT = `of-platform:global-store-registered:${DEALER_STORE_KEY}`;
const EMPTY_STATE = {
    selectedDealerCode: '',
    userResources: null,
    userRoles: [],
};
const EMPTY_RESOURCES = [];
const isRecord = (value) => typeof value === 'object' && value !== null;
const getLegacyUserResources = (value) => {
    if ('userResources' in value && value.userResources !== value) {
        return value.userResources;
    }
    const nestedState = value.state;
    if (isRecord(nestedState) &&
        'userResources' in nestedState &&
        nestedState.userResources !== value) {
        return nestedState.userResources;
    }
    return undefined;
};
const extractResources = (rawResources) => {
    if (rawResources === null || rawResources === undefined) {
        return EMPTY_RESOURCES;
    }
    if (Array.isArray(rawResources)) {
        return rawResources.filter((resource) => typeof resource === 'string');
    }
    if (!isRecord(rawResources)) {
        return EMPTY_RESOURCES;
    }
    const legacyResources = getLegacyUserResources(rawResources);
    if (legacyResources !== undefined) {
        return extractResources(legacyResources);
    }
    return Object.keys(rawResources);
};
const isResourcesLoaded = (rawResources) => {
    if (rawResources === null || rawResources === undefined) {
        return false;
    }
    if (Array.isArray(rawResources)) {
        return true;
    }
    if (!isRecord(rawResources)) {
        return false;
    }
    const legacyResources = getLegacyUserResources(rawResources);
    if (legacyResources !== undefined) {
        return isResourcesLoaded(legacyResources);
    }
    return true;
};
const getDealerStore = () => {
    if (typeof globalThis === 'undefined')
        return null;
    const store = globalThis[DEALER_STORE_KEY];
    if (!store || (typeof store !== 'object' && typeof store !== 'function'))
        return null;
    const candidate = store;
    if (typeof candidate.getState !== 'function' ||
        typeof candidate.subscribe !== 'function') {
        return null;
    }
    return candidate;
};
export function DealerPermissionsProvider({ children, }) {
    const subscribe = useCallback((listener) => {
        let unsubscribeFromStore;
        const bindStoreSubscription = () => {
            unsubscribeFromStore === null || unsubscribeFromStore === void 0 ? void 0 : unsubscribeFromStore();
            const store = getDealerStore();
            if (!store)
                return;
            unsubscribeFromStore = store.subscribe(listener);
        };
        const handleStoreRegistered = () => {
            bindStoreSubscription();
            listener();
        };
        bindStoreSubscription();
        if (typeof window !== 'undefined') {
            window.addEventListener(STORE_REGISTERED_EVENT, handleStoreRegistered);
        }
        return () => {
            unsubscribeFromStore === null || unsubscribeFromStore === void 0 ? void 0 : unsubscribeFromStore();
            unsubscribeFromStore = undefined;
            if (typeof window !== 'undefined') {
                window.removeEventListener(STORE_REGISTERED_EVENT, handleStoreRegistered);
            }
        };
    }, []);
    const storeState = useSyncExternalStore(subscribe, () => { var _a, _b; return (_b = (_a = getDealerStore()) === null || _a === void 0 ? void 0 : _a.getState()) !== null && _b !== void 0 ? _b : EMPTY_STATE; }, () => EMPTY_STATE);
    const { userRoles: roles, userResources: rawResources, selectedDealerCode, } = storeState;
    const resources = useMemo(() => extractResources(rawResources), [rawResources]);
    const dealerCode = selectedDealerCode || null;
    const isLoaded = isResourcesLoaded(rawResources);
    const value = useMemo(() => ({
        roles,
        resources,
        dealerCode,
        isLoaded,
    }), [roles, resources, dealerCode, isLoaded]);
    return (_jsx(PermissionsContext.Provider, { value: value, children: children }));
}
